/// <amd-module name="Wit/WorkItemForm/Common/Hooks/WorkItemFormPageContext" />
import { createContext, Provider, useContext } from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IFieldLookup } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export interface IWorkItemFormPageContext {
    focusDiscussion: boolean;
    updateFocusDiscussion: (shouldFocus: boolean) => void;
    pageContext: IVssPageContext | null;
    workItem: IWorkItem | null;
}

export const WorkItemFormPageContext: React.Context<IWorkItemFormPageContext> = createContext<IWorkItemFormPageContext>({
    focusDiscussion: false,
    pageContext: null,
    updateFocusDiscussion: () => {},
    workItem: null
});

export const WorkItemFormPageContextProvider: Provider<IWorkItemFormPageContext> = WorkItemFormPageContext.Provider;

export function useWorkItem(): IWorkItem {
    const workItem = useContext(WorkItemFormPageContext).workItem;
    if (!workItem) {
        throw new Error("useWorkItem can only be used inside a WorkItemFormPageContextProvider");
    }
    return workItem;
}

export function useAssetLocation(assetId: string): string {
    const pageContext = usePageContext();
    return getAssetLocation(pageContext, assetId) || "";
}

export function useWorkItemTrackingRestClient(): IWorkItemTrackingRestClient {
    const pageContext = usePageContext();
    return getWorkItemTrackingClient(pageContext);
}

/**
 * @param refName The ref name of the field
 * @returns Metadata for the given field ref name, including the field's display name, id, and type
 */
export function useFieldMetadata<T extends string>(refName: string): IFieldEntry & { referenceName: T };
/**
 * @param fieldId The ID of the field
 * @returns Metadata for the given field ID, including the field's display name, ref name, and type
 */
export function useFieldMetadata<T extends number>(fieldId: number): IFieldEntry & { id: T };
/**
 * @returns A map from field IDs or ref names to metadata including the field's display name, id, ref name, and type
 */
export function useFieldMetadata<T extends number>(): IFieldLookup;
export function useFieldMetadata(idOrName: string | number): IFieldEntry | undefined;
export function useFieldMetadata(idOrName?: string | number): IFieldEntry | IFieldLookup | undefined {
    const workItem = useWorkItem();
    const fieldLookup = workItem.getFieldLookup();
    return idOrName === undefined ? fieldLookup : fieldLookup[idOrName];
}
